import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PATTERN_LABELS, CURRENCIES } from "@/i18n/translations";
import { SHIFT_META } from "@/lib/shifts";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const PATTERNS = {
  classic_rotating: ["morning", "morning", "afternoon", "afternoon", "night", "night", "off", "off"],
  mans_off: ["morning", "afternoon", "night", "off"],
  five_mornings: ["morning", "morning", "morning", "morning", "morning", "off", "off"],
  continental: ["morning", "morning", "afternoon", "afternoon", "night", "night", "off"],
  nights_block: ["night", "night", "night", "off", "off"],
};

function PatternPreview({ seq }) {
  return (
    <div className="flex gap-1 mt-2">
      {seq.map((s, i) => (
        <span key={i} className="h-2 flex-1 rounded-full" style={{ backgroundColor: SHIFT_META[s].color }} />
      ))}
    </div>
  );
}

export default function Onboarding() {
  const { refresh } = useAuth();
  const { t, lang } = useI18n();
  const [step, setStep] = useState(0);
  const [pattern, setPattern] = useState("classic_rotating");
  const [anchor, setAnchor] = useState(new Date().toISOString().slice(0, 10));
  const [currency, setCurrency] = useState("EUR");
  const [rate, setRate] = useState(20);
  const [nightDiff, setNightDiff] = useState(25);
  const [saving, setSaving] = useState(false);

  const labels = PATTERN_LABELS[lang] || PATTERN_LABELS.en;

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        pattern: PATTERNS[pattern],
        pattern_name: pattern,
        anchor_date: anchor,
        currency,
        hourly_rate: Number(rate),
        night_diff_pct: Number(nightDiff),
        language: lang,
        onboarded: true,
      });
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSaving(false);
    }
  };

  const steps = [t("obPatternTitle"), t("obAnchorTitle"), t("obPayTitle")];

  return (
    <div className="min-h-screen bg-background flex flex-col px-5 py-6 max-w-md w-full mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <Moon className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-display font-extrabold">{t("obTitle")}</span>
        </div>
        <LanguageSwitcher />
      </div>

      <div className="mt-6 flex gap-2">
        {steps.map((_, i) => (
          <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-secondary"}`} />
        ))}
      </div>
      <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">
        {t("obStep")} {step + 1} / 3
      </p>

      <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="mt-4 flex-1">
        <h2 className="font-display text-2xl font-bold">{steps[step]}</h2>

        {step === 0 && (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-muted-foreground">{t("obPatternSub")}</p>
            {Object.keys(PATTERNS).map((key) => (
              <button
                key={key}
                data-testid={`pattern-option-${key}`}
                onClick={() => setPattern(key)}
                className={`w-full text-left rounded-xl border p-4 transition-colors ${pattern === key ? "border-primary bg-secondary/60" : "border-white/10 hover:border-white/25"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{labels[key]}</span>
                  {pattern === key && <Check className="h-4 w-4 text-primary" />}
                </div>
                <PatternPreview seq={PATTERNS[key]} />
              </button>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="mt-5 space-y-4">
            <p className="text-sm text-muted-foreground">{t("obAnchorSub")}</p>
            <div className="space-y-1.5">
              <Label htmlFor="anchor">{t("obAnchorTitle")}</Label>
              <Input id="anchor" type="date" data-testid="onboarding-anchor-input" value={anchor}
                onChange={(e) => setAnchor(e.target.value)} className="bg-secondary/70 border-white/10 h-12" />
            </div>
            <div className="rounded-xl border border-white/10 p-4">
              <PatternPreview seq={PATTERNS[pattern]} />
              <p className="mt-2 text-xs text-muted-foreground">{labels[pattern]}</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-5 space-y-4">
            <p className="text-sm text-muted-foreground">{t("obPaySub")}</p>
            <div className="space-y-1.5">
              <Label>{t("currency")}</Label>
              <div className="grid grid-cols-4 gap-2">
                {CURRENCIES.map((c) => (
                  <button key={c.code} data-testid={`currency-option-${c.code}`} onClick={() => setCurrency(c.code)}
                    className={`rounded-lg border py-2 text-sm font-semibold transition-colors ${currency === c.code ? "border-primary bg-secondary/60" : "border-white/10 hover:border-white/25"}`}>
                    {c.symbol}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="rate">{t("hourlyRate")}</Label>
                <Input id="rate" type="number" step="0.5" data-testid="onboarding-rate-input" value={rate}
                  onChange={(e) => setRate(e.target.value)} className="bg-secondary/70 border-white/10 h-12" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nd">{t("nightDiff")}</Label>
                <Input id="nd" type="number" step="5" data-testid="onboarding-nightdiff-input" value={nightDiff}
                  onChange={(e) => setNightDiff(e.target.value)} className="bg-secondary/70 border-white/10 h-12" />
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="mt-6 flex gap-3">
        {step > 0 && (
          <Button variant="outline" data-testid="onboarding-back-btn" onClick={() => setStep(step - 1)} className="h-12 border-white/15">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        {step < 2 ? (
          <Button data-testid="onboarding-next-btn" onClick={() => setStep(step + 1)} className="flex-1 h-12 font-semibold">
            {t("next")} <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button data-testid="onboarding-finish-btn" onClick={finish} disabled={saving} className="flex-1 h-12 font-semibold">
            {saving ? t("processing") : t("finish")}
          </Button>
        )}
      </div>
    </div>
  );
}
